import { getJson } from '../server';
import createSlidingKeyValueCache from '../sliding-key-value-cache';
import URLX from '../Url';
import { createOpenPromise, OpenPromise } from './open-promise';

export { useDynamicData } from './use-dynamic-data';

export const batchWaitMS = 32;

type CacheItem = { $cacheKey: string } & Scope.DynamicData<any>;

const cache = createSlidingKeyValueCache<CacheItem>(
  'dynamicdatacache',
  50,
  1000,
  (item, key) => (item.$cacheKey === key ? item : null),
  item => true,
  item => true,
  item => true,
  item => item.$cacheKey,
  // Only allow dropping something from the fast cache if we don't have any
  // data watchers on it
  key => !dataWatchers[key] || dataWatchers[key].length === 0,
);

export type DataUpdateCallback = () => any;

const languageInit = createOpenPromise<string>();
let requestedPropertiesBeforeLanguageInit: Array<Scope.DynamicData<{}>> = [];

export async function setLanguage(language: string) {
  languageInit.resolve(language);

  // Since onDataUpdate awaits languageInit we need to await it here as well
  // in order to have the data watchers registered correctly
  await languageInit;

  requestedPropertiesBeforeLanguageInit.forEach(property => callDataWatchers(getCacheKey(property, language)));
  requestedPropertiesBeforeLanguageInit = [];
}

// https://stackoverflow.com/questions/51040703/what-return-type-should-be-used-for-settimeout-in-typescript
let timer: ReturnType<typeof setTimeout> = -1 as any;
const dataWatchers: { [cacheKey: string]: DataUpdateCallback[] } = {};
let toResolve: { [cacheKey: string]: Scope.DynamicData<any> } = {};
const requestsInFlight: { [cacheKey: string]: OpenPromise<void> } = {};

export async function nukeCache() {
  await cache.nukeCache();

  Object.keys(dataWatchers).forEach(cacheKey => {
    if (dataWatchers[cacheKey]) {
      dataWatchers[cacheKey].forEach(l => l());
    }
  });
}

export function getFastCache() {
  return cache.getFastCache();
}

export function resolveDataSync<T>(property: Scope.DynamicData<T>): T | null {
  if (!languageInit.resolvedValue) {
    requestedPropertiesBeforeLanguageInit.push(property);
    return null;
  }
  const cacheKey = getCacheKey(property, languageInit.resolvedValue!);
  const fastCacheItem = cache.getFastCacheItem(cacheKey);
  if (fastCacheItem) {
    return (fastCacheItem as any) as T;
  }
  if (property.preloaded) {
    return property.preloaded;
  }
  return null;
}

export function createDataContext<T>(property: Scope.DynamicData<T>, context: Object): Scope.DynamicData<T> {
  return {
    ...property,
    context: {
      ...property.context,
      ...context,
    },
  };
}

export async function updateData<T>(property: Scope.DynamicData<T>, data: T) {
  await addToCache(getCacheKey(property, await languageInit), data as any);
}

export async function invalidateAndResolveData<T>(property: Scope.DynamicData<T>, callWatchersOnInvalidate = true) {
  await invalidateData(property, callWatchersOnInvalidate);
  return resolveData(property);
}

export async function invalidateData<T>(property: Scope.DynamicData<T>, willCallWatchers = true) {
  const cacheKey = getCacheKey(property, await languageInit);
  if (requestsInFlight[cacheKey]) {
    delete requestsInFlight[cacheKey];
  }
  await cache.clearCacheItem(cacheKey);
  if (willCallWatchers) {
    callDataWatchers(cacheKey);
  }
}

export async function resolveData<T>(property: Scope.DynamicData<T>, refreshIfCached = true): Promise<T> {
  const cacheKey = getCacheKey(property, await languageInit);

  if (requestsInFlight[cacheKey]) {
    await requestsInFlight[cacheKey];
    return (cache.getFastCacheItem(cacheKey) as any) as T;
  }

  const wasInFastCache = cache.getFastCacheItem(cacheKey) !== null;

  let networkResolveRequest: Promise<T> | null = null;

  // Refresh the cache, but only if the item doesn't exist in the
  // fast cache. If it's in the fast cache it means that we've either
  // placed a loaded response in the cache or previously loaded it over
  // the network and placed it in the fast cache.
  if (refreshIfCached && !wasInFastCache) {
    networkResolveRequest = resolveOverNetwork<T>(property);
  }

  return cache.getCacheItem(cacheKey).then(cached => {
    if (cached) {
      // If it wasn't in the fast cache it means that resolveDataSync() wouldn't have
      // returned it so we need to call the data watcher to let them know that we now
      // have a value
      if (!wasInFastCache) {
        callDataWatchers(cacheKey);
      }
      return (cached as any) as T;
    } else {
      return networkResolveRequest || resolveOverNetwork<T>(property);
    }
  });
}

export async function forceResolveFreshData<T>(property: Scope.DynamicData<T>) {
  const cacheKey = getCacheKey(property, await languageInit);
  if (requestsInFlight[cacheKey]) {
    await requestsInFlight[cacheKey];
  }
  return resolveOverNetwork(property);
}

async function resolveOverNetwork<T>(property: Scope.DynamicData<T>) {
  if (!property.type) {
    throw new Error(
      'Tried to resolve Dynamic Data but the data passed in was not a Dynamic Data property. It was: ' +
        JSON.stringify(property),
    );
  }
  const cacheKey = getCacheKey(property, await languageInit);
  const payload = {
    ...property,
  };
  delete payload.preloaded;
  delete payload.resolveMode;
  if (!payload.context) {
    delete payload.context;
  }

  if (!(cacheKey in toResolve)) {
    toResolve[cacheKey] = payload;
    requestsInFlight[cacheKey] = createOpenPromise();
  }
  clearTimeout(timer);
  timer = setTimeout(async () => {
    const resolvesToSend = toResolve;
    toResolve = {};

    // The sorting here isn't just because it's fun, it's because we need to ensure a determinitic format
    // for the json in the query string. The reason for that is that we use HTTP2 push/preloading of
    // dynamic data with resolveMode=auto. In order to do that we have to anticipate the exact url that
    // the client will make.
    // The same ordering (and removal of some properties) is done in the backend. If you change this in some
    // way please make sure that you make the same change in the backend.
    const sortedResolves = Object.values(resolvesToSend);
    sortDynamicDataResolves(sortedResolves);
    const url = new URLX('/resolvedynamicdata');
    url.searchParams.append('d', generateDynamicDataQueryString(sortedResolves));

    // We don't include the default headers here (such as X-Requested-With since that triggers a
    // bug in Chrome where a preloaded response isn't used)
    try {
      const results: any[] = await getJson(url, {}, false);

      Object.keys(resolvesToSend).forEach(async sentCacheKey => {
        const sentResolve = resolvesToSend[sentCacheKey];
        const index = sortedResolves.indexOf(sentResolve);
        await addToCache(sentCacheKey, results[index]);

        if (requestsInFlight[sentCacheKey]) {
          requestsInFlight[sentCacheKey].resolve(undefined);
          delete requestsInFlight[sentCacheKey];
        }
      });
    } catch (e) {
      Object.keys(resolvesToSend).forEach(sentCacheKey => {
        if (requestsInFlight[sentCacheKey]) {
          requestsInFlight[sentCacheKey].reject(e);
          delete requestsInFlight[sentCacheKey];
        }
      });
    }
  }, batchWaitMS);

  await requestsInFlight[cacheKey];
  return ((await cache.getFastCacheItem(cacheKey)) as any) as T;
}

export function sortDynamicDataResolves(resolves: Scope.DynamicData<any>[]) {
  resolves.sort((a, b) => {
    if (a.type < b.type) {
      return -1;
    }
    if (a.type > b.type) {
      return 1;
    }
    return 0;
  });
}

export function generateDynamicDataQueryString(sortedResolves: Scope.DynamicData<any>[]) {
  let lastType: string | undefined = undefined;
  const compressedResolvesJsons = sortedResolves.map(r => {
    let type: string | undefined = r.type;
    if (lastType !== type) {
      lastType = type;
    } else {
      type = undefined;
    }

    const props: string[] = [];
    if (type) {
      props.push('t:"' + type + '"');
    }
    if (r.context) {
      props.push('c:' + JSON.stringify(r.context));
    }
    let json = '{' + props.join(',') + '}';
    return json;
  });

  const allCompressedResolvesJson = '[' + compressedResolvesJsons.join(',') + ']';
  return allCompressedResolvesJson;
}

export function autoResolveDynamicData(json: any, promises?: Array<Promise<any>>) {
  let returnPromiseAll = false;
  if (!promises) {
    promises = [];
    returnPromiseAll = true;
  }

  if (!json) {
    return;
  } else if (Array.isArray(json)) {
    json.forEach(j => autoResolveDynamicData(j, promises));
  } else if (isDynamicData(json)) {
    if (json.preloaded) {
      updateData(json, json.preloaded);
    } else if (json.resolveMode === 'auto') {
      promises.push(resolveOverNetwork(json));
    }
  } else if (typeof json === 'object') {
    Object.keys(json).forEach(k => autoResolveDynamicData(json[k], promises));
  }

  if (returnPromiseAll) {
    return Promise.all(promises);
  } else {
    return Promise.resolve(null);
  }
}

function isDynamicData(obj: any): obj is Scope.DynamicData<any> {
  const dynamicData = obj as Scope.DynamicData<any>;
  return !!dynamicData.type && !!dynamicData.resolveMode;
}

function getCacheKey<T>(property: Scope.DynamicData<T>, keyPrefix: string) {
  const cacheContext: { [key: string]: any } = {};
  if (property.context) {
    const keys = Object.keys(property.context);
    keys.sort();
    keys.forEach(key => {
      cacheContext[key] = property.context[key];
    });
  }

  return keyPrefix + property.type + JSON.stringify(cacheContext);
}

export function onDataUpdate(property: Scope.DynamicData<any>, callback: DataUpdateCallback): () => void {
  if (!__BROWSER__) {
    return () => null;
  }
  languageInit.then(language => {
    const cacheKey = getCacheKey(property, language);
    if (!dataWatchers[cacheKey]) {
      dataWatchers[cacheKey] = [];
    }
    dataWatchers[cacheKey].push(callback);
  });
  return () => {
    languageInit.then(language => {
      const cacheKey = getCacheKey(property, language);
      const idx = dataWatchers[cacheKey].indexOf(callback);
      dataWatchers[cacheKey].splice(idx, 1);
    });
  };
}

async function addToCache(cacheKey: string, data: CacheItem) {
  data = {
    ...data,
    ...{ $cacheKey: cacheKey },
  };

  // We await languageInit here because otherwise we might set a cache item that gets thrown
  // out because of language mismatch. So what might happen is:
  // 1) I visist /en and get DD caches for that
  // 2) I go to /de and request DD items in German
  // 3) We get the German DD items, and place them in the cache
  // 4) The cache is now mixed with English and German entries
  // 5) languageInit will nuke the cache and remove the (valid) German entries
  // 6) We'd need to re-request the German entries
  await languageInit;
  try {
    await cache.setCacheItem(data, false);
  } catch (e) {
    console.error('Could not add to cache, cache key: ' + cacheKey, data);
  }
  callDataWatchers(cacheKey);
}

function callDataWatchers(cacheKey: string) {
  if (dataWatchers[cacheKey]) {
    dataWatchers[cacheKey].forEach(dw => dw());
  }
}
